import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Circle, Polyline, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Room, RoomEvent } from 'livekit-client';
import { API } from '../config';
import ZonePicker from '../components/ZonePicker';
import YolKorsat from '../components/YolKorsat';
import { tr } from '../til';

// Ozodlikni cheklash jazosidagi shaxslar ustidan masofaviy nazorat.
//
// Ekran uch savolga javob berish uchun qurilgan — inspektor ertalab
// panelni ochib, boshqa hech qayerga qaramasdan kunini rejalashtira olsin:
//   1. Nima bo'ldi?        -> ogohlantirishlar
//   2. Kimga borishim kerak? -> norma qarzdorligi (403-buyruq 84/85-band)
//   3. Qaysi muddat yonmoqda? -> prokuror navbati (O'RQ-938 24-modda)

const karta = {
  background: '#fff', borderRadius: '12px', padding: '20px',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)', marginBottom: '16px'
};

const btn = {
  padding: '8px 16px', border: 'none', borderRadius: '8px',
  fontSize: '13px', cursor: 'pointer', color: '#fff'
};

const smallBtn = {
  padding: '6px 12px', background: '#eef1f7', color: '#1a1a2e',
  border: '1px solid #ddd', borderRadius: '8px', fontSize: '12px', cursor: 'pointer'
};

const input = {
  width: '100%', padding: '9px 11px', border: '1px solid #ddd',
  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
};

const label = { display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' };

// Daraja ranglari. Qizil — darhol harakat, sariq — kuzatish,
// ko'k — ma'lumot uchun.
const RANG = {
  qizil: { fon: '#fdeaea', chiziq: '#d32f2f', matn: '#b71c1c' },
  sariq: { fon: '#fff8e1', chiziq: '#f9a825', matn: '#e65100' },
  kok:   { fon: '#e8f1fb', chiziq: '#1976d2', matn: '#0d47a1' }
};

const HODISA_NOMI = {
  uydan_chiqdi: tr("Uydan chiqdi"),
  gps_ochirildi: tr("GPS o'chirildi"),
  ruxsat_olindi: tr("Joylashuv ruxsati olib tashlandi"),
  soxta_gps: tr("Soxta GPS aniqlandi"),
  aloqa_yoq: tr("Aloqa yo'q"),
  batareya_kam: tr("Batareya kam"),
  qurilma_ozgardi: tr("Qurilma o'zgardi"),
  vaqt_ozgartirilgan: tr("Qurilma vaqti o'zgartirilgan")
};

// Selfi shubhali bo'lsa qizil ko'rsatamiz: orqa kameradan olingan
// (boshqa ekranni suratga olishning odatiy belgisi) yoki olingan vaqti
// qayd etilmagan (eski ilova yoki o'zgartirilgan surat).
const selfiOgoh = (t) => t.foto_kamera === 'orqa' || !t.foto_olingan_vaqt;

const NATIJA_NOMI = {
  uyda: tr("Uyda edi"),
  uyda_emas: tr("Uyda yo'q edi"),
  eshik_ochilmadi: tr("Eshik ochilmadi"),
  javob_bermadi: tr("Javob bermadi"),
  texnik_nosozlik: tr("Texnik nosozlik")
};

const vaqtMatni = (iso) => (iso ? new Date(iso).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
}) : '—');

const qanchaOtdi = (iso) => {
  if (!iso) return tr("hech qachon");
  const daq = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (daq < 1) return tr('hozir');
  if (daq < 60) return tr('{n} daq oldin', { n: daq });
  const soat = Math.floor(daq / 60);
  if (soat < 24) return tr('{n} soat oldin', { n: soat });
  return tr('{n} kun oldin', { n: Math.floor(soat / 24) });
};

// ---------------------------------------------------------------------------
// Mahkumning jonli holati — bitta qatorda ko'rinadigan eng muhim ma'lumot
// ---------------------------------------------------------------------------
const holatBelgisi = (m) => {
  if (!m.nazorat_yoqilgan) return { matn: tr("Ilova yo'q"), rang: '#9e9e9e' };
  if (m.aloqa_uzilgan) return { matn: tr("Aloqa yo'q ({n} daq)", { n: m.aloqa_daqiqa }), rang: '#d32f2f' };
  if (m.gps_yoqiq === false) return { matn: tr("GPS o'chiq"), rang: '#d32f2f' };
  if (m.uyda === true) return { matn: tr("Uyda"), rang: '#2e7d32' };
  if (m.uyda === false) {
    // Ruxsat oynasida chiqish qonuniy (JK 48-1-modda) — uni qizil
    // qilib ko'rsatish inspektorni chalg'itadi.
    return m.ruxsat_oynasida === true
      ? { matn: tr('Ishda ({n} m)', { n: m.uy_masofa_m }), rang: '#1976d2' }
      : { matn: tr('Uydan tashqarida ({n} m)', { n: m.uy_masofa_m }), rang: '#d32f2f' };
  }
  return { matn: tr("Ma'lumot yo'q"), rang: '#9e9e9e' };
};

const REJIM_NOMI = {
  toliq: tr("To'liq taqiq"),
  qisman: tr("Qisman cheklov")
};

// ---------------------------------------------------------------------------
// Modal qobig'i
// ---------------------------------------------------------------------------
function Modal({ sarlavha, children, yop, keng }) {
  return (
    <div
      onClick={yop}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '14px', padding: '22px',
          width: '100%', maxWidth: keng ? '760px' : '520px',
          maxHeight: '90vh', overflowY: 'auto'
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: '16px'
        }}>
          <h3 style={{ margin: 0, fontSize: '17px' }}>{sarlavha}</h3>
          <button onClick={yop} style={{
            ...smallBtn, padding: '4px 10px', fontSize: '16px', lineHeight: 1
          }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nazorat sozlamalari
// ---------------------------------------------------------------------------
function SozlamaModal({ mahkum, token, yop, yangila }) {
  const [f, setF] = useState({
    ochj_rejim: mahkum.ochj_rejim || 'toliq',
    uy_lat: mahkum.uy_lat ?? '',
    uy_lon: mahkum.uy_lon ?? '',
    uy_radius_m: mahkum.uy_radius_m ?? 150,
    telefon: mahkum.telefon || '',
    ish_boshlanish: (mahkum.ish_boshlanish || '').slice(0, 5),
    ish_tugash: (mahkum.ish_tugash || '').slice(0, 5),
    yol_daqiqa: mahkum.yol_daqiqa ?? 30,
    ish_kunlari: mahkum.ish_kunlari || '12345',
    nazorat_boshlanish: (mahkum.nazorat_boshlanish || '').slice(0, 5),
    nazorat_tugash: (mahkum.nazorat_tugash || '').slice(0, 5)
  });
  const [xato, setXato] = useState('');
  const [band, setBand] = useState(false);

  const saqla = async () => {
    setBand(true); setXato('');
    try {
      await axios.put(`${API}/probatsiya/ochj/mahkumlar/${mahkum.id}/sozlama`, f,
        { headers: { authorization: token } });
      yangila(); yop();
    } catch (err) {
      setXato(err.response?.data?.error || tr("Saqlashda xato"));
    } finally {
      setBand(false);
    }
  };

  const kunTanla = (k) => {
    const bor = f.ish_kunlari.includes(k);
    const yangi = bor
      ? f.ish_kunlari.replace(k, '')
      : (f.ish_kunlari + k).split('').sort().join('');
    setF({ ...f, ish_kunlari: yangi });
  };

  return (
    <Modal sarlavha={tr("Nazorat sozlamalari — {familiya} {ism}", { familiya: mahkum.familiya, ism: mahkum.ism })} yop={yop} keng>
      <div style={{ marginBottom: '16px' }}>
        <label style={label}>{tr("Jazo rejimi (JK 48-1-modda)")}</label>
        {[
          { k: 'toliq', n: tr("To'liq taqiq"),
            i: tr("Uy-joyni bu yoki bu bahona bilan tark etish butunlay taqiqlanadi. Tekshiruv normasi: oyiga kamida 3 marta (403-buyruq, 84-band)") },
          { k: 'qisman', n: tr("Qisman cheklov"),
            i: tr("Ish/o'qish vaqti va yo'l vaqtidan tashqari chiqish taqiqlanadi. Tekshiruv normasi: oyiga kamida 1 marta (403-buyruq, 85-band)") }
        ].map((r) => (
          <div
            key={r.k}
            onClick={() => setF({ ...f, ochj_rejim: r.k })}
            style={{
              border: `2px solid ${f.ochj_rejim === r.k ? '#1976d2' : '#e0e0e0'}`,
              background: f.ochj_rejim === r.k ? '#f3f8ff' : '#fff',
              borderRadius: '10px', padding: '10px 12px',
              marginBottom: '8px', cursor: 'pointer'
            }}
          >
            <div style={{ fontWeight: 600, fontSize: '14px' }}>{r.n}</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>{r.i}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: '14px' }}>
        <label style={label}>
          {tr("Uy-joy manzili — xaritadan bosib tanlang")}
        </label>
        <ZonePicker
          latitude={f.uy_lat}
          longitude={f.uy_lon}
          radius={Number(f.uy_radius_m) || 150}
          onPick={(lat, lon) => setF({ ...f, uy_lat: lat, uy_lon: lon })}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
        <div>
          <label style={label}>{tr("Radius (metr)")}</label>
          <input style={input} type="number" min="30" max="2000"
            value={f.uy_radius_m}
            onChange={(e) => setF({ ...f, uy_radius_m: e.target.value })} />
          <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
            {tr("Kichik radius yolg'on ogohlantirish beradi: uy ichida GPS aniqligi 50–100 metrgacha yomonlashadi")}
          </div>
        </div>
        <div>
          <label style={label}>{tr("Telefon")}</label>
          <input style={input} value={f.telefon} placeholder="+998 __ ___ __ __"
            onChange={(e) => setF({ ...f, telefon: e.target.value })} />
        </div>
      </div>

      {/* Nazorat soatlari — rejimdan qat'i nazar. Amalda hukmlarning
          ko'pchiligida aniq vaqt yoziladi (22:00 dan, ba'zan 20:00 dan),
          kunduzi esa mahkum ishda bo'ladi va bu qonuniy. */}
      <div style={{
        border: '1px solid #cfe3ff', background: '#f7fbff',
        borderRadius: '10px', padding: '14px', marginBottom: '14px'
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
          {tr("Nazorat vaqti (hukmda ko'rsatilgan)")}
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
          {tr("Hukmda odatda aniq soat yoziladi — masalan 22:00 dan 06:00 gacha uy-joyda bo'lish. Shu vaqtdan tashqarida uydan chiqish buzilish hisoblanmaydi: mahkum ish yoki o'qishda bo'ladi.")}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
          <div>
            <label style={label}>{tr("Soat ... dan")}</label>
            <input style={input} type="time" value={f.nazorat_boshlanish}
              onChange={(e) => setF({ ...f, nazorat_boshlanish: e.target.value })} />
          </div>
          <div>
            <label style={label}>{tr("... gacha")}</label>
            <input style={input} type="time" value={f.nazorat_tugash}
              onChange={(e) => setF({ ...f, nazorat_tugash: e.target.value })} />
          </div>
          <button
            onClick={() => setF({ ...f, nazorat_boshlanish: '', nazorat_tugash: '' })}
            style={{ ...smallBtn, background: '#eef1f7', color: '#1a1a2e' }}>
            {tr("Tozalash")}
          </button>
        </div>

        <div style={{ marginTop: '10px', fontSize: '12px', color: f.nazorat_boshlanish && f.nazorat_tugash ? '#1976d2' : '#c2621a' }}>
          {f.nazorat_boshlanish && f.nazorat_tugash
            ? tr("Nazorat: {dan} dan {gacha} gacha. Qolgan vaqtda uydan chiqish taqiqlanmaydi.", { dan: f.nazorat_boshlanish, gacha: f.nazorat_tugash })
            : tr("Vaqt ko'rsatilmagan — nazorat kecha-kunduz davom etadi va kunduzi ishdagi mahkum ham buzuvchi deb belgilanadi.")}
        </div>
      </div>

      {f.ochj_rejim === 'qisman' && (
        <div style={{
          border: '1px solid #ffe082', background: '#fffdf5',
          borderRadius: '10px', padding: '14px', marginBottom: '14px'
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
            {tr("Ruxsat etilgan vaqt")}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
            {tr("JK 48-1-moddasi: ish/o'qish vaqti va yo'l vaqtida uydan tashqarida bo'lish buzilish emas. Bu vaqt ko'rsatilmasa tizim qonuniy chiqishni buzilishdan ajrata olmaydi.")}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <div>
              <label style={label}>{tr("Ish boshlanishi")}</label>
              <input style={input} type="time" value={f.ish_boshlanish}
                onChange={(e) => setF({ ...f, ish_boshlanish: e.target.value })} />
            </div>
            <div>
              <label style={label}>{tr("Ish tugashi")}</label>
              <input style={input} type="time" value={f.ish_tugash}
                onChange={(e) => setF({ ...f, ish_tugash: e.target.value })} />
            </div>
            <div>
              <label style={label}>{tr("Yo'l vaqti (daq)")}</label>
              <input style={input} type="number" min="0" max="300" value={f.yol_daqiqa}
                onChange={(e) => setF({ ...f, yol_daqiqa: e.target.value })} />
            </div>
          </div>

          <div style={{ marginTop: '12px' }}>
            <label style={label}>{tr("Ish kunlari")}</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['1', '2', '3', '4', '5', '6', '7'].map((k, i) => (
                <button key={k} onClick={() => kunTanla(k)} style={{
                  ...smallBtn, flex: 1,
                  background: f.ish_kunlari.includes(k) ? '#1976d2' : '#eef1f7',
                  color: f.ish_kunlari.includes(k) ? '#fff' : '#1a1a2e'
                }}>
                  {[tr('Du'), tr('Se'), tr('Ch'), tr('Pa'), tr('Ju'), tr('Sh'), tr('Ya')][i]}
                </button>
              ))}
            </div>
          </div>

          {f.ish_boshlanish && f.ish_tugash && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#1976d2' }}>
              {tr("Chiqishga ruxsat:")} <b>
                {(() => {
                  const [s, d] = f.ish_boshlanish.split(':').map(Number);
                  const yol = Number(f.yol_daqiqa) || 0;
                  const dan = (s * 60 + d - yol + 1440) % 1440;
                  const [s2, d2] = f.ish_tugash.split(':').map(Number);
                  const gacha = (s2 * 60 + d2 + yol) % 1440;
                  const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
                  return `${fmt(dan)} — ${fmt(gacha)}`;
                })()}
              </b> {tr("(yo'l vaqti hisobga olingan)")}
            </div>
          )}
        </div>
      )}

      {xato && <div style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '10px' }}>{xato}</div>}

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button onClick={yop} style={smallBtn}>{tr("Bekor qilish")}</button>
        <button onClick={saqla} disabled={band}
          style={{ ...btn, background: band ? '#9e9e9e' : '#1976d2' }}>
          {band ? tr("Saqlanmoqda…") : tr("Saqlash")}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tekshiruv qayd etish
// ---------------------------------------------------------------------------
function TekshiruvModal({ mahkum, token, yop, yangila }) {
  const [turi, setTuri] = useState('tashrif');
  const [natija, setNatija] = useState('uyda');
  const [izoh, setIzoh] = useState('');
  const [joy, setJoy] = useState(null);
  const [joyHolat, setJoyHolat] = useState('kutilmoqda');
  const [xato, setXato] = useState('');
  const [band, setBand] = useState(false);
  const [natijaJavob, setNatijaJavob] = useState(null);

  // Tashrifda xodimning o'z koordinatasi olinadi — bu tashrif haqiqatan
  // shu manzilda bo'lganining dalili. Nazorat ikki tomonlama bo'lishi kerak.
  useEffect(() => {
    if (turi !== 'tashrif' || !navigator.geolocation) {
      setJoyHolat('yoq');
      return;
    }
    setJoyHolat('kutilmoqda');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setJoy({ lat: p.coords.latitude, lon: p.coords.longitude, aniqlik: p.coords.accuracy });
        setJoyHolat('olindi');
      },
      () => setJoyHolat('rad'),
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }, [turi]);

  const NATIJALAR = turi === 'tashrif'
    ? ['uyda', 'uyda_emas', 'eshik_ochilmadi']
    : ['uyda', 'uyda_emas', 'javob_bermadi', 'texnik_nosozlik'];

  const saqla = async () => {
    setBand(true); setXato('');
    try {
      const res = await axios.post(
        `${API}/probatsiya/ochj/mahkumlar/${mahkum.id}/tekshiruv`,
        {
          turi, natija, izoh,
          xodim_lat: joy?.lat, xodim_lon: joy?.lon
        },
        { headers: { authorization: token } }
      );
      setNatijaJavob(res.data);
      yangila();
    } catch (err) {
      setXato(err.response?.data?.error || tr("Saqlashda xato"));
    } finally {
      setBand(false);
    }
  };

  if (natijaJavob) {
    return (
      <Modal sarlavha={tr("Tekshiruv qayd etildi")} yop={yop}>
        <div style={{
          background: '#e8f5e9', borderRadius: '10px', padding: '14px', marginBottom: '14px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#2e7d32' }}>
            ✓ {tr("Saqlandi")}
          </div>
          {natijaJavob.norma && (
            <div style={{ fontSize: '13px', marginTop: '8px' }}>
              {tr("Oylik norma:")} <b>{natijaJavob.norma.bajarildi} / {natijaJavob.norma.kerak}</b>
              <div style={{ fontSize: '11px', color: '#666' }}>{natijaJavob.norma.band}</div>
            </div>
          )}
        </div>

        {natijaJavob.buzilish && (
          <div style={{
            background: RANG.qizil.fon, borderLeft: `4px solid ${RANG.qizil.chiziq}`,
            borderRadius: '8px', padding: '12px', marginBottom: '14px', fontSize: '13px'
          }}>
            <b style={{ color: RANG.qizil.matn }}>{tr("Buzilish deb belgilandi.")}</b>
            <div style={{ marginTop: '4px', color: '#555' }}>
              {tr("Qoidabuzarlik aniqlangan kundan e'tiboran")} <b>{tr("1 oydan kechiktirmay")}</b>
              {tr("intizomiy jazo (ogohlantirish) qo'llanadi — O'RQ-938, 62-modda.")}
            </div>
          </div>
        )}

        {natijaJavob.prokuror_xabari && (
          <div style={{
            background: RANG.sariq.fon, borderLeft: `4px solid ${RANG.sariq.chiziq}`,
            borderRadius: '8px', padding: '12px', fontSize: '13px'
          }}>
            <b style={{ color: RANG.sariq.matn }}>{tr("Prokuror xabarnomasi yaratildi")}</b>
            <div style={{ marginTop: '4px', color: '#555' }}>
              {tr("Muddat:")} <b>{vaqtMatni(natijaJavob.prokuror_xabari.muddat)}</b><br />
              {natijaJavob.prokuror_xabari.eslatma}
            </div>
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#777' }}>
              {tr("U \"Prokuror\" bo'limida navbatda turadi. Yuborgach belgilab qo'ying.")}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={yop} style={{ ...btn, background: '#1976d2' }}>{tr("Yopish")}</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal sarlavha={tr("Tekshiruv — {familiya} {ism}", { familiya: mahkum.familiya, ism: mahkum.ism })} yop={yop}>
      <div style={{ marginBottom: '14px' }}>
        <label style={label}>{tr("Tekshiruv turi")}</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { k: 'tashrif', n: tr("Jismoniy tashrif") },
            { k: 'telefon', n: tr("Telefon orqali") }
          ].map((t) => (
            <button key={t.k} onClick={() => { setTuri(t.k); setNatija('uyda'); }} style={{
              ...smallBtn, flex: 1, padding: '10px',
              background: turi === t.k ? '#1976d2' : '#eef1f7',
              color: turi === t.k ? '#fff' : '#1a1a2e'
            }}>{t.n}</button>
          ))}
        </div>
      </div>

      {turi === 'tashrif' && (
        <div style={{
          background: '#f3f8ff', borderRadius: '8px', padding: '10px 12px',
          marginBottom: '14px', fontSize: '12px'
        }}>
          {joyHolat === 'kutilmoqda' && tr("Joylashuvingiz aniqlanmoqda…")}
          {joyHolat === 'olindi' && (
            <span style={{ color: '#2e7d32' }}>
              ✓ {tr("Joylashuvingiz olindi (aniqlik ±{round} m) — tashrif dalili sifatida saqlanadi", { round: Math.round(joy.aniqlik) })}
            </span>
          )}
          {joyHolat === 'rad' && (
            <span style={{ color: '#e65100' }}>
              {tr("Joylashuv olinmadi. Tekshiruv baribir saqlanadi, lekin tashrifning manzil dalili bo'lmaydi.")}
            </span>
          )}
          {joyHolat === 'yoq' && tr("Bu qurilmada joylashuv mavjud emas")}
        </div>
      )}

      <div style={{ marginBottom: '14px' }}>
        <label style={label}>{tr("Natija")}</label>
        {NATIJALAR.map((n) => (
          <div key={n} onClick={() => setNatija(n)} style={{
            border: `2px solid ${natija === n ? '#1976d2' : '#e0e0e0'}`,
            background: natija === n ? '#f3f8ff' : '#fff',
            borderRadius: '8px', padding: '9px 12px', marginBottom: '6px',
            cursor: 'pointer', fontSize: '14px'
          }}>{NATIJA_NOMI[n]}</div>
        ))}
      </div>

      {turi === 'tashrif' && natija !== 'eshik_ochilmadi' && (
        <div style={{
          background: RANG.sariq.fon, borderRadius: '8px',
          padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: '#555'
        }}>
          {tr("Uyga kirilgani uchun")} <b>{tr("24 soat ichida prokurorni xabardor qilish shart")}</b>
          {tr("(O'RQ-938, 24-modda). Saqlagach tizim muddatni o'zi sanaydi.")}
        </div>
      )}

      <div style={{ marginBottom: '14px' }}>
        <label style={label}>{tr("Izoh")}</label>
        <textarea style={{ ...input, minHeight: '70px', resize: 'vertical' }}
          value={izoh} onChange={(e) => setIzoh(e.target.value)}
          placeholder={tr("Kim bilan uchrashildi, nima aniqlandi…")} />
      </div>

      {xato && <div style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '10px' }}>{xato}</div>}

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button onClick={yop} style={smallBtn}>{tr("Bekor qilish")}</button>
        <button onClick={saqla} disabled={band}
          style={{ ...btn, background: band ? '#9e9e9e' : '#2e7d32' }}>
          {band ? tr("Saqlanmoqda…") : tr("Qayd etish")}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Signallar xaritasi
// ---------------------------------------------------------------------------
function XaritaModal({ mahkum, token, yop }) {
  const [malumot, setMalumot] = useState(null);
  const [kun, setKun] = useState(1);
  const [xato, setXato] = useState('');

  useEffect(() => {
    axios.get(`${API}/probatsiya/ochj/mahkumlar/${mahkum.id}/signallar?kun=${kun}`,
      { headers: { authorization: token } })
      .then((r) => { setMalumot(r.data); setXato(''); })
      .catch((e) => setXato(e.response?.data?.error || tr("Yuklashda xato")));
  }, [mahkum.id, kun, token]);

  const nuqtalar = (malumot?.signallar || [])
    .filter((s) => s.latitude != null)
    .map((s) => [Number(s.latitude), Number(s.longitude)]);

  const uy = malumot?.uy;
  const markaz = uy?.lat != null ? [Number(uy.lat), Number(uy.lon)]
    : nuqtalar[0] || [41.311, 69.279];

  return (
    <Modal sarlavha={tr("Harakat tarixi — {familiya} {ism}", { familiya: mahkum.familiya, ism: mahkum.ism })} yop={yop} keng>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {[1, 3, 7, 30].map((k) => (
          <button key={k} onClick={() => setKun(k)} style={{
            ...smallBtn,
            background: kun === k ? '#1976d2' : '#eef1f7',
            color: kun === k ? '#fff' : '#1a1a2e'
          }}>{tr("{k} kun", { k })}</button>
        ))}
      </div>

      {xato && <div style={{ color: '#d32f2f', fontSize: '13px' }}>{xato}</div>}

      {uy?.lat == null && (
        <div style={{
          background: RANG.sariq.fon, borderRadius: '8px', padding: '12px',
          fontSize: '13px', marginBottom: '12px'
        }}>
          {tr("Uy koordinatasi belgilanmagan — masofa hisoblanmaydi. \"Sozlash\" tugmasi orqali xaritadan tanlang.")}
        </div>
      )}

      <div style={{ height: '400px', borderRadius: '10px', overflow: 'hidden' }}>
        <MapContainer center={markaz} zoom={15} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap"
          />
          {uy?.lat != null && (
            <>
              <Marker position={[Number(uy.lat), Number(uy.lon)]}>
                <Popup>{tr("Uy-joy")}</Popup>
              </Marker>
              <Circle
                center={[Number(uy.lat), Number(uy.lon)]}
                radius={Number(uy.radius) || 150}
                pathOptions={{ color: '#2e7d32', fillOpacity: 0.08 }}
              />
            </>
          )}
          {nuqtalar.length > 1 && (
            <Polyline positions={nuqtalar} pathOptions={{ color: '#1976d2', weight: 2, opacity: 0.6 }} />
          )}
          {(malumot?.signallar || []).filter((s) => s.latitude != null).map((s) => (
            <Circle
              key={s.id}
              center={[Number(s.latitude), Number(s.longitude)]}
              radius={6}
              pathOptions={{
                // Uyda — yashil, ruxsat oynasida tashqarida — ko'k,
                // ruxsatsiz tashqarida — qizil.
                color: s.soxta_gps ? '#7b1fa2'
                  : s.uyda ? '#2e7d32'
                  : s.ruxsat_oynasida ? '#1976d2' : '#d32f2f',
                fillOpacity: 0.9
              }}
            >
              <Popup>
                <div style={{ fontSize: '12px' }}>
                  {vaqtMatni(s.vaqt || s.created_at)}<br />
                  {tr("Uydan:")} {s.uy_masofa_m ?? '—'} m<br />
                  {tr("Aniqlik: ±{round} m", { round: Math.round(s.aniqlik_m ?? 0) })}<br />
                  {tr("Batareya:")} {s.batareya ?? '—'}%
                  {s.soxta_gps && <div style={{ color: '#7b1fa2' }}><b>{tr("Soxta GPS")}</b></div>}
                  {/* Internet yo'q bo'lganda telefonda saqlanib, keyin
                      yuborilgan signal. O'lchangan payti ko'rsatilgan. */}
                  {s.kechikkan && (
                    <div style={{ color: '#888' }}>
                      {tr("Aloqa tiklangach yuborilgan")}
                    </div>
                  )}
                </div>
              </Popup>
            </Circle>
          ))}
        </MapContainer>
      </div>

      <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '12px', color: '#666' }}>
        <span>🟢 {tr("Uyda")}</span>
        <span>🔵 {tr("Ruxsat etilgan vaqtda tashqarida")}</span>
        <span>🔴 {tr("Ruxsatsiz tashqarida")}</span>
        <span>🟣 {tr("Soxta GPS")}</span>
      </div>
      <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
        {tr("Vaqt — signal telefonda o'lchangan payt. Internet yo'q bo'lganda signal saqlanib, aloqa tiklangach yuboriladi.")}
      </div>
      <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
        {tr("Jami")} {malumot?.signallar?.length ?? 0} {tr("ta signal")}
      </div>
    </Modal>
  );
}


// ---------------------------------------------------------------------------
// Dalolatnomani chop etish
// ---------------------------------------------------------------------------
// Hujjat ALOHIDA OYNADA ochiladi. Sabab: panelning uslublari (rang, soya,
// yon menyu) chop etishda qog'ozga tushib, hujjatni buzardi. Yangi oyna
// esa faqat hujjatni o'z ichiga oladi va A4 ga toza chiqadi.
const dalolatnomaniChop = (d, rasmlar = []) => {
  const q = d.qiymatlar;
  const toza = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const qator = (nom, qiymat) => (qiymat
    ? `<tr><td class="n">${nom}</td><td class="v">${toza(qiymat)}</td></tr>`
    : '');
  const imzolangan = !!d.imzo;

  const html = `<!doctype html>
<html lang="uz"><head><meta charset="utf-8">
<title>Dalolatnoma ${d.raqam}</title>
<style>
  @page { size: A4; margin: 18mm 16mm; }
  body { font-family: "Times New Roman", Georgia, serif; font-size: 12pt;
         color: #000; line-height: 1.45; }
  h1 { font-size: 15pt; text-align: center; margin: 0 0 4px; letter-spacing: 1px; }
  .ost { text-align: center; font-size: 11pt; margin-bottom: 4px; }
  .raqam { text-align: center; font-size: 11pt; margin-bottom: 18px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
  td { vertical-align: top; padding: 5px 4px; border-bottom: 1px solid #ddd; }
  td.n { width: 34%; font-weight: bold; }
  .asos { font-size: 10pt; color: #333; margin-bottom: 20px;
          border-left: 3px solid #999; padding-left: 10px; }
  .imzo { margin-top: 26px; }
  .imzo .qator { margin-bottom: 22px; }
  .imzo .chiziq { display: inline-block; border-bottom: 1px solid #000;
                  width: 190px; margin: 0 8px; }
  .imzo .izoh { font-size: 9pt; color: #555; }
  .ogoh { border: 1px solid #000; padding: 8px 10px; margin-bottom: 16px;
          font-size: 11pt; }
  .rasmlar { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 18px; }
  .rasm { width: 47%; }
  .rasm img { width: 100%; border: 1px solid #999; }
  .rasm .tag { font-size: 9pt; color: #444; margin-top: 3px; }
  .eimzo { border: 1px solid #000; padding: 10px 12px; margin-top: 22px;
           font-size: 10.5pt; }
  .eimzo .xesh { font-family: "Courier New", monospace; font-size: 8.5pt;
                 word-break: break-all; color: #333; }
  .xolis { margin: 6px 0 0 0; padding-left: 18px; }
  @media print { .yashir { display: none; } }
</style></head><body>

<h1>${d.shakl.sarlavha.nom}</h1>
<div class="ost">${d.shakl.sarlavha.ost}</div>
<div class="raqam">№ ${d.raqam}</div>

${d.tekshiruv.buzilish
  ? '<div class="ogoh"><b>Qoidabuzarlik aniqlangan.</b></div>' : ''}

<table>
  ${d.shakl.bandlar.map((b) => qator(b.nom, q[b.kalit])).join('')}
</table>

<div class="asos">
  <b>Huquqiy asos:</b><br>${d.shakl.asos.join('<br>')}
  ${d.norma ? `<br><b>Tekshiruv davriyligi:</b> ${d.norma.davriylik} (${d.norma.band})` : ''}
</div>

${rasmlar.length ? `
<div><b>Ilova: fotosuratlar (${rasmlar.length} ta)</b></div>
<div class="rasmlar">
  ${rasmlar.map((r, i) => `
    <div class="rasm">
      <img src="${r.manba}" alt="surat">
      <div class="tag">${i + 1}-surat${r.izoh ? '. ' + toza(r.izoh) : ''}${
        r.vaqt ? '. ' + new Date(r.vaqt).toLocaleString('ru-RU') : ''}</div>
    </div>`).join('')}
</div>` : ''}

${(d.xolislar || []).length ? `
<div><b>Ishtirok etgan holis shaxslar:</b></div>
<ol class="xolis">
  ${d.xolislar.map((x) => `<li>${toza(x.fio)}${x.manzil ? ', ' + toza(x.manzil) : ''}${
    x.telefon ? ', tel: ' + toza(x.telefon) : ''}</li>`).join('')}
</ol>` : ''}

${imzolangan ? `
<div class="eimzo">
  <b>Hujjat elektron shaklda imzolangan</b><br>
  Imzoladi: ${toza(d.imzo.xodim)}<br>
  Vaqti: ${new Date(d.imzo.vaqt).toLocaleString('ru-RU')}<br>
  ${d.imzo.shaxs_matni ? toza(d.imzo.shaxs_matni) + '<br>' : ''}
  ${d.imzo.shaxs_izohi ? 'Izoh: ' + toza(d.imzo.shaxs_izohi) + '<br>' : ''}
  <div style="margin-top:6px">${toza(d.imzo.izoh || '')}</div>
  <div class="xesh">SHA-256: ${toza(d.xesh || '')}</div>
  ${d.xesh_togri === false
    ? '<div style="margin-top:6px"><b>DIQQAT: nazorat yig\'indisi mos kelmadi.</b></div>'
    : ''}
</div>` : `
<div class="ogoh">QORALAMA — hujjat hali imzolanmagan.</div>
<div class="imzo">
  ${d.shakl.imzolar.map((i) => `
    <div class="qator">
      ${i.nom}: <span class="chiziq"></span> <span class="chiziq"></span>
      <div class="izoh">(imzo) &nbsp;&nbsp;&nbsp; (F.I.Sh.)
        ${i.izoh ? '<br>' + i.izoh : ''}</div>
    </div>`).join('')}
</div>`}

<div class="yashir" style="margin-top:24px;text-align:center">
  <button onclick="window.print()" style="padding:10px 22px;font-size:14px">
    Chop etish
  </button>
</div>
</body></html>`;

  const oyna = window.open('', '_blank', 'width=860,height=1000');
  if (!oyna) {
    alert(tr("Brauzer yangi oynani bloklab qo'ydi. Bu sayt uchun ")
      + tr("qalqib chiquvchi oynalarga ruxsat bering."));
    return;
  }
  oyna.document.write(html);
  oyna.document.close();
};

// ---------------------------------------------------------------------------
// Dalolatnoma: ko'rish, surat qo'shish, elektron imzolash
// ---------------------------------------------------------------------------
// Mahkum uyida bo'lmasa hujjatni u imzolay olmaydi. Shu holatda
// dalolatnomaning ishonchliligi ikki narsa bilan ta'minlanadi: tashrif
// surati va holis shaxslar (403-buyruq 15-ilova, O'RQ-938 62-modda).
//
// Imzolangan hujjat O'ZGARMAYDI: matni imzo paytida muzlatib saqlanadi va
// undan SHA-256 nazorat yig'indisi olinadi.
function DalolatnomaModal({ tekshiruv, token, yop }) {
  const [d, setD] = useState(null);
  const [xato, setXato] = useState(null);
  const [xolislar, setXolislar] = useState([]);
  const [shaxs, setShaxs] = useState('');
  const [izoh, setIzoh] = useState('');
  const [band, setBand] = useState(null);

  const h = { headers: { authorization: token } };
  const yol = `${API}/probatsiya/ochj/tekshiruvlar/${tekshiruv.id}/dalolatnoma`;

  const yukla = useCallback(async () => {
    try {
      const r = await axios.get(yol, { headers: { authorization: token } });
      setD(r.data);
      if (r.data.holat === 'qoralama') {
        const t = r.data.talablar;
        setShaxs((s) => s || t.shaxs_imzosi_variantlari[0]);
        setXolislar((x) => (x.length ? x : Array.from(
          { length: t.xolis_shart ? t.xolis_eng_kam : 0 },
          () => ({ fio: '', manzil: '', telefon: '' }))));
      }
    } catch (err) {
      setXato(err.response?.data?.error || tr("Dalolatnomani ochib bo'lmadi"));
    }
  }, [yol, token]);

  useEffect(() => { yukla(); }, [yukla]);

  const suratQosh = async (e) => {
    const fayl = e.target.files?.[0];
    e.target.value = '';
    if (!fayl) return;
    setBand('foto');
    try {
      const fd = new FormData();
      fd.append('foto', fayl);
      await axios.post(`${API}/probatsiya/ochj/tekshiruvlar/${tekshiruv.id}/foto`, fd, h);
      await yukla();
    } catch (err) {
      alert(err.response?.data?.error || tr("Surat yuklanmadi"));
    } finally {
      setBand(null);
    }
  };

  const imzola = async () => {
    const tola = xolislar
      .map((x) => ({ fio: x.fio.trim(), manzil: x.manzil.trim(), telefon: x.telefon.trim() }))
      .filter((x) => x.fio || x.manzil);
    if (!window.confirm(tr("Dalolatnoma imzolangach o'zgartirib bo'lmaydi. Imzolansinmi?"))) return;
    setBand('imzo');
    try {
      await axios.post(yol, { shaxs_imzosi: shaxs, shaxs_izohi: izoh || null, xolislar: tola }, h);
      await yukla();
    } catch (err) {
      alert(err.response?.data?.error || tr("Imzolab bo'lmadi"));
    } finally {
      setBand(null);
    }
  };

  // Suratlar chop etish oynasiga data: havola bo'lib tushadi — yangi oyna
  // bizning tokenimizni yubora olmaydi, ya'ni oddiy havola ochilmasdi.
  const chop = async () => {
    setBand('chop');
    try {
      const rasmlar = [];
      for (const f of d.fotolar || []) {
        const r = await axios.get(`${API.replace(/\/api$/, '')}${f.url}`,
          { headers: { authorization: token }, responseType: 'blob' });
        rasmlar.push({
          manba: await new Promise((res) => {
            const fr = new FileReader();
            fr.onload = () => res(fr.result);
            fr.readAsDataURL(r.data);
          }),
          izoh: f.izoh, vaqt: f.vaqt,
        });
      }
      dalolatnomaniChop(d, rasmlar);
    } catch (err) {
      alert(tr("Suratlarni yuklab bo'lmadi"));
    } finally {
      setBand(null);
    }
  };

  const xolisOzgar = (i, maydon, qiymat) => setXolislar(
    (x) => x.map((q, n) => (n === i ? { ...q, [maydon]: qiymat } : q)));

  const SHAXS_NOMI = {
    qoydi: tr("Mahkum imzo qo'ydi"),
    bosh_tortdi: tr("Mahkum imzolashdan bosh tortdi"),
    yoq_edi: tr("Mahkum yashash joyida bo'lmagan"),
  };

  return (
    <Modal sarlavha={tr("Dalolatnoma")} yop={yop} keng>
      {xato && <div style={{ color: '#d32f2f', fontSize: '13px' }}>{xato}</div>}
      {!d && !xato && <div style={{ color: '#888' }}>{tr("Yuklanmoqda…")}</div>}

      {d && (
        <>
          <div style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>
            № {d.raqam} · {d.qiymatlar?.shaxs?.split(',')[0]}
          </div>

          {d.holat === 'imzolangan' ? (
            <div style={{
              background: '#eef7ee', border: '1px solid #cfe3cf',
              borderRadius: '10px', padding: '12px', fontSize: '13px'
            }}>
              <b>{tr("Elektron imzolangan")}</b><br />
              {d.imzo.xodim} · {new Date(d.imzo.vaqt).toLocaleString('ru-RU')}<br />
              {d.imzo.shaxs_matni}
              {d.xolislar?.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <b>{tr("Holis shaxslar:")}</b>
                  <ol style={{ margin: '4px 0 0 18px', padding: 0 }}>
                    {d.xolislar.map((x, i) => (
                      <li key={i}>{x.fio}{x.manzil ? `, ${x.manzil}` : ''}
                        {x.telefon ? `, ${x.telefon}` : ''}</li>
                    ))}
                  </ol>
                </div>
              )}
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#666',
                wordBreak: 'break-all' }}>
                {tr("SHA-256: {xesh}", { xesh: d.xesh })}
              </div>
              {d.xesh_togri === false && (
                <div style={{ color: '#d32f2f', marginTop: '6px' }}>
                  <b>{tr("DIQQAT:")}</b> {tr("nazorat yig'indisi mos kelmadi — bazadagi yozuv o'zgartirilgan bo'lishi mumkin.")}
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{
                background: '#fff8e6', border: '1px solid #f0dfae',
                borderRadius: '10px', padding: '10px 12px', fontSize: '12px',
                marginBottom: '14px'
              }}>
                {tr("Hujjat hali imzolanmagan. Imzolangach u")} <b>{tr("o'zgarmaydi")}</b> — {tr("xatosi bo'lsa yangi tekshiruv qayd etiladi.")}
              </div>

              <div style={{ marginBottom: '14px' }}>
                <div style={label}>{tr("Mahkumning imzosi")}</div>
                {d.talablar.shaxs_imzosi_variantlari.map((v) => (
                  <label key={v} style={{ display: 'block', fontSize: '13px', marginBottom: '4px' }}>
                    <input type="radio" checked={shaxs === v} onChange={() => setShaxs(v)}
                      style={{ marginRight: '6px' }} />
                    {SHAXS_NOMI[v] || v}
                  </label>
                ))}
                {d.talablar.shaxs_imzosi_variantlari.length === 1 && (
                  <div style={{ fontSize: '11px', color: '#888' }}>
                    {tr("Tekshiruv natijasiga ko'ra mahkum hujjatni imzolay olmaydi.")}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '14px' }}>
                <div style={label}>
                  {tr("Holis shaxslar")}
                  {d.talablar.xolis_shart
                    ? tr(" — kamida {xolis_eng_kam} ta shart", { xolis_eng_kam: d.talablar.xolis_eng_kam })
                    : tr(" (ixtiyoriy)")}
                </div>
                {xolislar.map((x, i) => (
                  <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                    <input placeholder={tr("F.I.Sh.")} value={x.fio}
                      onChange={(e) => xolisOzgar(i, 'fio', e.target.value)}
                      style={{ ...input, flex: 2 }} />
                    <input placeholder={tr("Manzili")} value={x.manzil}
                      onChange={(e) => xolisOzgar(i, 'manzil', e.target.value)}
                      style={{ ...input, flex: 2 }} />
                    <input placeholder={tr("Telefon")} value={x.telefon}
                      onChange={(e) => xolisOzgar(i, 'telefon', e.target.value)}
                      style={{ ...input, flex: 1 }} />
                  </div>
                ))}
                {xolislar.length < d.talablar.xolis_eng_kop && (
                  <button style={smallBtn} onClick={() => setXolislar(
                    (q) => [...q, { fio: '', manzil: '', telefon: '' }])}>
                    + {tr("Holis shaxs")}
                  </button>
                )}
              </div>

              <div style={{ marginBottom: '14px' }}>
                <div style={label}>{tr("Qo'shimcha izoh (ixtiyoriy)")}</div>
                <input value={izoh} onChange={(e) => setIzoh(e.target.value)}
                  placeholder={tr("Masalan: xonadonda hech kim bo'lmadi")} style={input} />
              </div>
            </>
          )}

          <div style={{ marginTop: '16px' }}>
            <div style={label}>{tr("Fotosuratlar ({soni} ta)", { soni: (d.fotolar || []).length })}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {(d.fotolar || []).map((f, i) => (
                <div key={i} style={{
                  fontSize: '12px', background: '#eef1f7', borderRadius: '8px',
                  padding: '6px 10px'
                }}>
                  {i + 1}- {tr("surat")}{f.izoh ? ` · ${f.izoh}` : ''}
                </div>
              ))}
              {(d.fotolar || []).length === 0 && (
                <div style={{ fontSize: '12px', color: '#888' }}>{tr("Surat qo'shilmagan")}</div>
              )}
            </div>
            {d.holat === 'qoralama' && (
              <label style={{ ...smallBtn, display: 'inline-block' }}>
                {band === 'foto' ? tr("Yuklanmoqda…") : tr("+ Surat qo'shish")}
                <input type="file" accept="image/*" onChange={suratQosh}
                  disabled={band === 'foto'} style={{ display: 'none' }} />
              </label>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
            {d.holat === 'qoralama' && (
              <button onClick={imzola} disabled={band === 'imzo'}
                style={{ ...btn, background: '#2e7d32' }}>
                {band === 'imzo' ? tr("Imzolanmoqda…") : tr("Elektron imzolash")}
              </button>
            )}
            <button onClick={chop} disabled={band === 'chop'}
              style={{ ...btn, background: '#1a1a2e' }}>
              {band === 'chop' ? tr("Tayyorlanmoqda…") : tr("Chop etish")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Nazorat fotosi
// ---------------------------------------------------------------------------
// Rasm oddiy <img src> bilan ochilmaydi: u autentifikatsiya talab qiladi
// va <img> teg sarlavha yubora olmaydi. Shuning uchun axios bilan yuklab,
// brauzer xotirasidagi havolaga aylantiramiz.
function FotoModal({ url, token, yop }) {
  const [manba, setManba] = useState(null);
  const [xato, setXato] = useState('');

  useEffect(() => {
    let obj;
    axios.get(url, { headers: { authorization: token }, responseType: 'blob' })
      .then((r) => {
        obj = URL.createObjectURL(r.data);
        setManba(obj);
      })
      .catch(() => setXato(tr("Rasmni ochib bo'lmadi")));
    // Xotirani bo'shatamiz — aks holda ko'p rasm ochilsa brauzer og'irlashadi.
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [url, token]);

  return (
    <Modal sarlavha={tr("Tekshiruv fotosi")} yop={yop}>
      {xato && <div style={{ color: '#d32f2f', fontSize: '13px' }}>{xato}</div>}
      {!manba && !xato && <div style={{ color: '#888' }}>{tr("Yuklanmoqda…")}</div>}
      {manba && (
        <img src={manba} alt={tr("Tekshiruv fotosi")}
          style={{ width: '100%', borderRadius: '10px' }} />
      )}
      <div style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>
        {tr("Bu shaxsga doir maxsus ma'lumot (O'RQ-938, 47-modda). Ochilgani jurnalga yozildi.")}
      </div>
    </Modal>
  );
}


// ---------------------------------------------------------------------------
// Selfilar arxivi — kunlar bo'yicha
// ---------------------------------------------------------------------------
// Onlayn tekshiruv selfisi dalil bo'lgani uchun u yo'qolmaydi va
// kunma-kun ko'rinib turadi: inspektor ham, rahbariyat ham "o'sha kuni
// qanday surat kelgan edi" degan savolga qaytib javob topa olsin.
//
// Rasm <img src> bilan ochilmaydi (token kerak), shuning uchun har bir
// surat axios bilan yuklanib, brauzer xotirasidagi havolaga aylanadi.
function SelfiRasm({ url, token, alt, uslub }) {
  const [manba, setManba] = useState(null);

  useEffect(() => {
    let obj; let tirik = true;
    axios.get(url, { headers: { authorization: token }, responseType: 'blob' })
      .then((r) => { if (!tirik) return; obj = URL.createObjectURL(r.data); setManba(obj); })
      .catch(() => {});
    return () => { tirik = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url, token]);

  if (!manba) {
    return <div style={{ ...uslub, background: '#eef1f7' }} />;
  }
  return <img src={manba} alt={alt} style={uslub} />;
}

// ---------------------------------------------------------------------------
// Bitta tekshiruv kartochkasi
// ---------------------------------------------------------------------------
// Ro'yxat mahkum bo'yicha guruhlangani uchun kartochka alohida
// komponentga ajratildi: u ham guruh ichida, ham kelajakda boshqa
// ekranda ishlatilishi mumkin.
function TekshiruvKarta({ t, setFoto, setDalolat }) {
  const TUR = { tashrif: tr("Tashrif"), onlayn: tr("Onlayn"), telefon: tr("Telefon") };
  return (
              <div key={t.id} style={{
                border: '1px solid #eee',
                borderLeft: `4px solid ${t.buzilish ? RANG.qizil.chiziq : '#c8e6c9'}`,
                borderRadius: '10px', padding: '12px 14px', marginBottom: '8px',
                display: 'flex', justifyContent: 'space-between',
                gap: '12px', flexWrap: 'wrap', alignItems: 'center'
              }}>
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>
                    {t.familiya} {t.ism}
                    <span style={{
                      marginLeft: '8px', fontSize: '11px', fontWeight: 500,
                      padding: '2px 8px', borderRadius: '20px',
                      background: '#eef1f7', color: '#555'
                    }}>{TUR[t.turi] || t.turi}</span>
                    {t.buzilish && (
                      <span style={{
                        marginLeft: '6px', fontSize: '11px', fontWeight: 600,
                        padding: '2px 8px', borderRadius: '20px',
                        background: RANG.qizil.fon, color: RANG.qizil.matn
                      }}>{tr("Buzilish")}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                    {NATIJA_NOMI[t.natija] || tr("Natija belgilanmagan")}
                    {' · '}{vaqtMatni(t.created_at)}
                    {t.user_nomi && ` · ${t.user_nomi}`}
                  </div>
                  {t.izoh && (
                    <div style={{ fontSize: '12px', color: '#555', marginTop: '4px' }}>
                      {t.izoh}
                    </div>
                  )}
                  {t.turi === 'onlayn' && (t.selfi_topshirigi || t.foto) && (
                    <div style={{
                      marginTop: '6px', background: '#f6f8fb', borderRadius: '8px',
                      padding: '8px 10px', fontSize: '12px', color: '#555', lineHeight: 1.5
                    }}>
                      {t.selfi_topshirigi && (
                        <div>
                          {tr("Topshiriq:")} <b>{t.selfi_topshirigi}</b>
                          {' — '}{tr("surat shunga mos kelishi kerak")}
                        </div>
                      )}
                      {t.foto && (
                        <div style={{ color: selfiOgoh(t) ? RANG.qizil.matn : '#777' }}>
                          {t.foto_kamera === 'orqa' && tr("Orqa kameradan olingan — boshqa ekranni suratga olgan bo'lishi mumkin")}
                          {t.foto_kamera === 'old' && tr("Old kameradan")}
                          {!t.foto_kamera && tr("Kamera turi noma'lum")}
                          {t.foto_olingan_vaqt
                            ? ` · ${tr("olingan {vaqt}", { vaqt: new Date(t.foto_olingan_vaqt).toLocaleTimeString() })}`
                            : ` · ${tr("olingan vaqti qayd etilmagan")}`}
                        </div>
                      )}
                    </div>
                  )}

                  {(t.xodim_masofa_m != null || t.mahkum_masofa_m != null) && (
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                      {t.xodim_masofa_m != null && tr("Xodim uydan {xodim_masofa_m} m", { xodim_masofa_m: t.xodim_masofa_m })}
                      {t.xodim_masofa_m != null && t.mahkum_masofa_m != null && ' · '}
                      {t.mahkum_masofa_m != null && tr("Mahkum uydan {mahkum_masofa_m} m", { mahkum_masofa_m: t.mahkum_masofa_m })}
                      {t.mahkum_soxta_gps && tr(" · SOXTA GPS")}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {t.foto && (
                    <button onClick={() => setFoto(t.foto)} style={smallBtn}>
                      {tr("Foto")}
                    </button>
                  )}
                  <button onClick={() => setDalolat(t)}
                    style={{
                      ...btn, padding: '7px 13px', fontSize: '12px',
                      background: t.dalolatnoma_imzolangan ? '#2e7d32' : '#1a1a2e'
                    }}>
                    {t.dalolatnoma_imzolangan
                      ? tr("✓ Dalolatnoma № {dalolatnoma_raqami}", { dalolatnoma_raqami: t.dalolatnoma_raqami })
                      : tr("Dalolatnoma tuzish")}
                  </button>
                </div>
              </div>
  );
}

function SelfiArxiv({ tekshiruvlar, token, ochish }) {
  const selfilar = tekshiruvlar.filter((t) => t.turi === 'onlayn' && t.foto);

  // Kunlar bo'yicha guruhlaymiz. Eng yangisi yuqorida.
  const kunlar = [];
  for (const t of selfilar) {
    const kun = new Date(t.created_at).toLocaleDateString();
    const bor = kunlar.find((k) => k.kun === kun);
    if (bor) bor.qatorlar.push(t); else kunlar.push({ kun, qatorlar: [t] });
  }

  if (selfilar.length === 0) {
    return (
      <div style={{ color: '#888', fontSize: '14px' }}>
        {tr("Hali selfi kelmagan. Onlayn tekshiruv yuborilganda bu yerda kunlar bo'yicha saqlanadi.")}
      </div>
    );
  }

  return (
    <div>
      {kunlar.map((k) => (
        <div key={k.kun} style={{ marginBottom: '18px' }}>
          <div style={{
            fontSize: '13px', fontWeight: 600, color: '#1a1a2e', marginBottom: '8px'
          }}>
            {k.kun} · {tr("{soni} ta selfi", { soni: k.qatorlar.length })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {k.qatorlar.map((t) => {
              const ogoh = selfiOgoh(t);
              return (
                <button key={t.id} onClick={() => ochish(t.foto)} style={{
                  padding: 0, border: `2px solid ${ogoh ? RANG.qizil.chiziq : '#e3e8ef'}`,
                  borderRadius: '10px', background: '#fff', cursor: 'pointer',
                  width: '150px', overflow: 'hidden', textAlign: 'left'
                }}>
                  <SelfiRasm url={`${API}${t.foto}`} token={token} alt={tr("Selfi")}
                    uslub={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block' }} />
                  <div style={{ padding: '6px 8px', fontSize: '11px', color: '#555', lineHeight: 1.45 }}>
                    <b>{new Date(t.created_at).toLocaleTimeString()}</b>
                    {' · '}{NATIJA_NOMI[t.natija] || tr("Natija belgilanmagan")}
                    <div style={{ color: '#888' }}>{t.familiya} {t.ism}</div>
                    {t.selfi_topshirigi && (
                      <div style={{ color: '#666' }}>{t.selfi_topshirigi}</div>
                    )}
                    {ogoh && (
                      <div style={{ color: RANG.qizil.matn, fontWeight: 600 }}>
                        {t.foto_kamera === 'orqa' ? tr("orqa kamera") : tr("vaqtsiz")}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Qo'ng'iroq oynasi
// ---------------------------------------------------------------------------
// Brauzerdan mahkum telefoniga ovozli qo'ng'iroq. Meetings.js dagi
// LiveKit naqshi bilan bir xil — u yerda allaqachon ishlab turgani uchun
// shu tasdiqlangan yo'ldan yurilgan.
//
// Oqim: POST /qongiroq -> inspektor DARHOL xonaga ulanadi (mahkum javob
// berishini kutayotganda ham tayyor turadi) -> har 2 soniyada
// GET /qongiroqlar/:id so'ralib, mahkum javob berdimi yoki rad etdimi
// kuzatiladi.
function QongiroqModal({ mahkum, token, yop, yangila }) {
  const [holat, setHolat] = useState('boshlanmoqda');
  const [xato, setXato] = useState('');
  const [mikrofon, setMikrofon] = useState(true);
  const [vaqt, setVaqt] = useState(0);
  const [qongiroqId, setQongiroqId] = useState(null);
  const [ogohlantirish, setOgohlantirish] = useState(null);

  const roomRef = useRef(null);
  const audioRef = useRef(null);
  const pollRef = useRef(null);
  const taymerRef = useRef(null);
  // Oyna yopilgach eski so'rovlar holatni o'zgartirib yubormasin.
  const tirikRef = useRef(true);

  // Faqat bir marta ishga tushsin.
  const boshlandiRef = useRef(false);

  useEffect(() => {
    if (boshlandiRef.current) return;
    boshlandiRef.current = true;

    const h = { headers: { authorization: token } };

    const boshla = async () => {
      try {
        const r = await axios.post(
          `${API}/probatsiya/ochj/mahkumlar/${mahkum.id}/qongiroq`, {}, h);
        if (!tirikRef.current) return;

        setQongiroqId(r.data.qongiroq_id);
        setOgohlantirish(r.data.ogohlantirish || null);
        setHolat('chaqirilmoqda');

        // --- LiveKit xonasiga ulanish ---
        const room = new Room({ adaptiveStream: false, dynacast: false });
        roomRef.current = room;

        room
          .on(RoomEvent.Disconnected, () => {
            if (tirikRef.current) setHolat((oldin) =>
              oldin === 'tugadi' ? oldin : 'tugadi');
          })
          // Mahkum ovozi kelganda uni sahifaga ulaymiz — ulanmasa
          // brauzer hech narsa eshittirmaydi.
          .on(RoomEvent.TrackSubscribed, (track) => {
            if (track.kind === 'audio' && audioRef.current) {
              audioRef.current.appendChild(track.attach());
            }
          })
          .on(RoomEvent.TrackUnsubscribed, (track) => {
            track.detach().forEach((el) => el.remove());
          });

        await room.connect(r.data.livekit_url, r.data.token);
        // Brauzerlar avtomatik ovoz ijrosini to'sadi — foydalanuvchi
        // tugma bosgani uchun shu yerda ruxsat so'ray olamiz.
        await room.startAudio().catch(() => {});
        await room.localParticipant.setMicrophoneEnabled(true);

        // --- Mahkum javob berishini kuzatish ---
        pollRef.current = setInterval(async () => {
          try {
            const q = await axios.get(
              `${API}/probatsiya/ochj/qongiroqlar/${r.data.qongiroq_id}`, h);
            if (!tirikRef.current) return;

            if (q.data.holat === 'javob_berildi') {
              clearInterval(pollRef.current);
              setHolat('suhbat');
              taymerRef.current = setInterval(
                () => tirikRef.current && setVaqt((v) => v + 1), 1000);
            } else if (['rad_etildi', 'javobsiz', 'tugadi'].includes(q.data.holat)) {
              clearInterval(pollRef.current);
              setHolat(q.data.holat);
              roomRef.current?.disconnect();
            }
          } catch (_) {
            // Tarmoq sakrashi — keyingi urinishda qayta so'raladi.
          }
        }, 2000);
      } catch (err) {
        if (!tirikRef.current) return;
        setXato(err.response?.data?.error || tr("Qo'ng'iroqni boshlab bo'lmadi"));
        setHolat('xato');
      }
    };

    boshla();

    return () => {
      tirikRef.current = false;
      clearInterval(pollRef.current);
      clearInterval(taymerRef.current);
      roomRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mikrofonAlmashtir = async () => {
    const yangi = !mikrofon;
    await roomRef.current?.localParticipant?.setMicrophoneEnabled(yangi);
    setMikrofon(yangi);
  };

  const tugat = async () => {
    clearInterval(pollRef.current);
    clearInterval(taymerRef.current);
    roomRef.current?.disconnect();

    if (qongiroqId) {
      try {
        await axios.put(`${API}/probatsiya/ochj/qongiroqlar/${qongiroqId}/tugat`,
          {}, { headers: { authorization: token } });
      } catch (_) {
        // Tarmoq xatosi bo'lsa ham oyna yopiladi — serverdagi navbatchi
        // osilib qolgan qo'ng'iroqni baribir yopadi.
      }
    }
    yangila();
    yop();
  };

  const vaqtMatni = `${String(Math.floor(vaqt / 60)).padStart(2, '0')}:${String(vaqt % 60).padStart(2, '0')}`;

  const HOLAT_MATNI = {
    boshlanmoqda: tr('Ulanmoqda…'),
    chaqirilmoqda: tr('Chaqirilmoqda…'),
    suhbat: vaqtMatni,
    rad_etildi: tr("Mahkum qo'ng'iroqni rad etdi"),
    javobsiz: tr('Javob berilmadi'),
    tugadi: tr('Suhbat tugadi'),
    xato: xato,
  };

  const tugagan = ['rad_etildi', 'javobsiz', 'tugadi', 'xato'].includes(holat);

  return (
    <Modal sarlavha={tr("Qo'ng'iroq — {familiya} {ism}", { familiya: mahkum.familiya, ism: mahkum.ism })} yop={tugagan ? yop : undefined}>
      {/* Mahkum ovozi shu yerga ulanadi. Ko'rinmaydi, lekin kerak. */}
      <div ref={audioRef} style={{ display: 'none' }} />

      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <div style={{
          width: '84px', height: '84px', borderRadius: '50%', margin: '0 auto 18px',
          background: holat === 'suhbat' ? '#e8f5e9' : '#eef1f7',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px'
        }}>
          {holat === 'suhbat' ? '🎤' : tugagan ? '📵' : '📞'}
        </div>

        <div style={{
          fontSize: holat === 'suhbat' ? '26px' : '16px',
          fontWeight: holat === 'suhbat' ? 700 : 500,
          color: holat === 'xato' ? '#d32f2f' : '#1a1a2e',
          fontFamily: holat === 'suhbat' ? 'monospace' : 'inherit'
        }}>
          {HOLAT_MATNI[holat]}
        </div>

        {holat === 'chaqirilmoqda' && (
          <div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
            {tr("Mahkum telefonida jiringlayapti. 45 soniyada javob bermasa — \"javob berilmadi\" deb qayd etiladi.")}
          </div>
        )}

        {ogohlantirish && holat === 'chaqirilmoqda' && (
          <div style={{
            marginTop: '12px', background: RANG.sariq.fon, borderRadius: '8px',
            padding: '10px 12px', fontSize: '12px', color: '#666', textAlign: 'left'
          }}>{ogohlantirish}</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
        {holat === 'suhbat' && (
          <button onClick={mikrofonAlmashtir} style={{
            ...smallBtn, padding: '10px 18px',
            background: mikrofon ? '#eef1f7' : RANG.qizil.fon,
            color: mikrofon ? '#1a1a2e' : RANG.qizil.matn
          }}>
            {mikrofon ? tr("🎤 Mikrofon yoniq") : tr("🔇 Mikrofon o'chiq")}
          </button>
        )}

        {tugagan ? (
          <button onClick={yop} style={{ ...btn, background: '#1976d2', padding: '10px 24px' }}>
            {tr("Yopish")}
          </button>
        ) : (
          <button onClick={tugat} style={{ ...btn, background: '#d32f2f', padding: '10px 24px' }}>
            {tr("Tugatish")}
          </button>
        )}
      </div>

      <div style={{ fontSize: '11px', color: '#999', marginTop: '16px', textAlign: 'center' }}>
        {tr("Suhbat \"telefon\" turidagi tekshiruv sifatida avtomatik qayd etiladi. Bu 84/85-banddagi jismoniy tashrif normasiga kirmaydi.")}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Asosiy sahifa
// ---------------------------------------------------------------------------
function Nazorat() {
  const [tab, setTab] = useState('panel');
  const [panel, setPanel] = useState(null);
  const [mahkumlar, setMahkumlar] = useState([]);
  const [prokuror, setProkuror] = useState([]);
  const [xato, setXato] = useState('');
  const [band, setBand] = useState(null);

  const [sozlama, setSozlama] = useState(null);
  const [tekshiruv, setTekshiruv] = useState(null);
  const [xarita, setXarita] = useState(null);
  const [yol, setYol] = useState(null);
  const [kod, setKod] = useState(null);
  const [tekshiruvlar, setTekshiruvlar] = useState([]);
  const [ochiqTekshiruv, setOchiqTekshiruv] = useState(null);
  // Tekshiruvlar bo'limi ikki ko'rinishda: oddiy ro'yxat va selfilar arxivi.
  const [korinish, setKorinish] = useState('royxat');
  const [foto, setFoto] = useState(null);
  const [dalolat, setDalolat] = useState(null);
  const [qongiroq, setQongiroq] = useState(null);

  const token = localStorage.getItem('token');
  const h = { headers: { authorization: token } };
  const birinchiRef = useRef(true);

  const yukla = useCallback(async () => {
    try {
      const [p, m, pr, t] = await Promise.all([
        axios.get(`${API}/probatsiya/ochj/panel`, h),
        axios.get(`${API}/probatsiya/ochj/mahkumlar`, h),
        axios.get(`${API}/probatsiya/ochj/prokuror?holat=kutilmoqda`, h),
        axios.get(`${API}/probatsiya/ochj/tekshiruvlar`, h)
      ]);
      setPanel(p.data); setMahkumlar(m.data); setProkuror(pr.data);
      setTekshiruvlar(t.data);
      setXato('');
    } catch (err) {
      setXato(err.response?.data?.error || tr("Ma'lumotni olishda xato"));
    } finally {
      birinchiRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { yukla(); }, [yukla]);

  // Ogohlantirish o'zidan chiqsin — inspektor sahifani yangilab o'tirmaydi.
  useEffect(() => {
    const t = setInterval(yukla, 20000);
    return () => clearInterval(t);
  }, [yukla]);

  const hodisaBelgila = async (id, holat) => {
    setBand(id);
    try {
      await axios.put(`${API}/probatsiya/ochj/hodisalar/${id}`, { holat }, h);
      await yukla();
    } catch (err) {
      setXato(err.response?.data?.error || tr("Xato"));
    } finally {
      setBand(null);
    }
  };

  const onlaynBoshla = async (m) => {
    setBand(`onlayn-${m.id}`);
    try {
      const r = await axios.post(`${API}/probatsiya/ochj/mahkumlar/${m.id}/onlayn`, {}, h);
      alert(r.data.ogohlantirish
        || tr("So'rov yuborildi. Mahkum 15 daqiqa ichida javob berishi kerak."));
      await yukla();
    } catch (err) {
      alert(err.response?.data?.error || tr("Xato"));
    } finally {
      setBand(null);
    }
  };

  const kodBer = async (m) => {
    setBand(`kod-${m.id}`);
    try {
      const r = await axios.post(`${API}/probatsiya/ochj/mahkumlar/${m.id}/qurilma`, {}, h);
      setKod({ mahkum: m, ...r.data });
      await yukla();
    } catch (err) {
      alert(err.response?.data?.error || tr("Xato"));
    } finally {
      setBand(null);
    }
  };

  // Telefon almashtirilganda yoki mahkum hisobdan chiqarilganda kerak.
  // Server ham xuddi shu amalni bajaradi: qurilma nofaol bo'ladi,
  // ishlatilmagan kod bekor qilinadi, ochiq ogohlantirishlar yopiladi.
  // Uzilgach mahkum nazoratsiz qoladi — shuning uchun tasdiq so'raymiz.
  const qurilmaUz = async (m) => {
    // Matn bitta bo'lak: tr() ga qo'shib yasalgan satr berilmaydi —
    // tarjima yig'uvchi (scripts/til-yigish.js) uni topa olmaydi.
    if (!window.confirm(tr(
      "{familiya} {ism} telefoni nazoratdan uzilsinmi?\n\nSignal kelmay qoladi. Yangi telefon ulash uchun \"Ilova kodi\" tugmasi bilan yangi kod berasiz.",
      { familiya: m.familiya, ism: m.ism }
    ))) return;
    setBand(`uz-${m.id}`);
    try {
      const r = await axios.delete(`${API}/probatsiya/ochj/mahkumlar/${m.id}/qurilma`, h);
      alert(r.data.message || tr("Qurilma biriktiruvi bekor qilindi"));
      await yukla();
    } catch (err) {
      alert(err.response?.data?.error || tr("Xato"));
    } finally {
      setBand(null);
    }
  };


  const prokurorYubor = async (p, usul) => {
    setBand(`prok-${p.id}`);
    try {
      const r = await axios.put(`${API}/probatsiya/ochj/prokuror/${p.id}`,
        { usul, qabul_qiluvchi: 'Tuman prokuraturasi' }, h);
      alert(r.data.message);
      await yukla();
    } catch (err) {
      alert(err.response?.data?.error || tr("Xato"));
    } finally {
      setBand(null);
    }
  };

  const TABLAR = [
    { k: 'panel', n: tr("Panel"), son: panel?.hodisalar?.length },
    { k: 'mahkumlar', n: tr("Nazoratdagilar"), son: mahkumlar.length },
    { k: 'tekshiruvlar', n: tr("Tekshiruvlar"), son: tekshiruvlar.length },
    { k: 'prokuror', n: tr("Prokuror"), son: prokuror.length }
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '4px' }}>
        <h2 style={{ margin: 0 }}>{tr("Masofaviy nazorat")}</h2>
        <span style={{ fontSize: '12px', color: '#888' }}>
          {tr("Ozodlikni cheklash jazosi · O'RQ-938 34-modda, 403-son buyruq 84–85-band")}
        </span>
      </div>
      <div style={{ fontSize: '12px', color: '#888', marginBottom: '16px' }}>
        {tr("Ma'lumot har 20 soniyada yangilanadi")}
      </div>

      {xato && (
        <div style={{
          ...karta, background: RANG.qizil.fon, color: RANG.qizil.matn, padding: '12px 16px'
        }}>{xato}</div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {TABLAR.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            ...smallBtn, padding: '9px 16px', fontSize: '13px',
            background: tab === t.k ? '#1a1a2e' : '#eef1f7',
            color: tab === t.k ? '#fff' : '#1a1a2e'
          }}>
            {t.n}{t.son > 0 ? ` (${t.son})` : ''}
          </button>
        ))}
      </div>

      {/* ---------------- PANEL ---------------- */}
      {tab === 'panel' && panel && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px', marginBottom: '16px'
          }}>
            {[
              { n: tr("To'liq taqiq"), v: panel.umumiy?.toliq ?? 0, r: '#d32f2f' },
              { n: tr("Qisman cheklov"), v: panel.umumiy?.qisman ?? 0, r: '#f9a825' },
              { n: tr("Ilova o'rnatilgan"), v: panel.umumiy?.ilova_bor ?? 0, r: '#2e7d32' },
              { n: tr("Ochiq ogohlantirish"), v: panel.hodisalar?.length ?? 0, r: '#1976d2' }
            ].map((s) => (
              <div key={s.n} style={{ ...karta, marginBottom: 0, padding: '16px' }}>
                <div style={{ fontSize: '26px', fontWeight: 700, color: s.r }}>{s.v}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{s.n}</div>
              </div>
            ))}
          </div>

          <div style={karta}>
            <h3 style={{ marginTop: 0, fontSize: '15px' }}>{tr("Ogohlantirishlar")}</h3>
            {panel.hodisalar.length === 0 && (
              <div style={{ color: '#888', fontSize: '14px', padding: '12px 0' }}>
                {tr("Ochiq ogohlantirish yo'q — hammasi joyida.")}
              </div>
            )}
            {panel.hodisalar.map((hd) => {
              const r = RANG[hd.daraja] || RANG.kok;
              return (
                <div key={hd.id} style={{
                  background: r.fon, borderLeft: `4px solid ${r.chiziq}`,
                  borderRadius: '8px', padding: '12px 14px', marginBottom: '8px',
                  display: 'flex', justifyContent: 'space-between', gap: '12px',
                  alignItems: 'flex-start', flexWrap: 'wrap'
                }}>
                  <div style={{ flex: 1, minWidth: '220px' }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: r.matn }}>
                      {HODISA_NOMI[hd.turi] || hd.turi} — {hd.familiya} {hd.ism}
                    </div>
                    <div style={{ fontSize: '13px', color: '#555', marginTop: '3px' }}>
                      {hd.tafsilot}
                    </div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                      {vaqtMatni(hd.boshlangan)} · {qanchaOtdi(hd.boshlangan)}
                      {hd.ochj_rejim && ` · ${REJIM_NOMI[hd.ochj_rejim]}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button disabled={band === hd.id}
                      onClick={() => hodisaBelgila(hd.id, 'korildi')} style={smallBtn}>
                      {tr("Ko'rdim")}
                    </button>
                    <button disabled={band === hd.id}
                      onClick={() => hodisaBelgila(hd.id, 'yopildi')} style={smallBtn}>
                      {tr("Yopish")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={karta}>
            <h3 style={{ marginTop: 0, fontSize: '15px' }}>
              {tr("Tashrif normasi bo'yicha qarzdorlik")}
            </h3>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
              {tr("Faqat")} <b>{tr("jismoniy tashrif")}</b> {tr("hisoblanadi. Onlayn va telefon tekshiruvi qo'shimcha nazorat — u 84/85-banddagi normani to'ldirmaydi. Oy oxiriga {qolgan_kun} kun qoldi.", { qolgan_kun: panel.oy?.qolgan_kun })}
            </div>
            {panel.qarzdorlar.length === 0 && (
              <div style={{ color: '#2e7d32', fontSize: '14px' }}>
                {tr("Barcha normalar bajarilgan.")}
              </div>
            )}
            {panel.qarzdorlar.map((q) => (
              <div key={q.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px', borderRadius: '8px', marginBottom: '6px',
                background: q.shoshilinch ? RANG.qizil.fon : '#f7f8fa', gap: '12px'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>
                    {q.familiya} {q.ism}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888' }}>
                    {REJIM_NOMI[q.ochj_rejim]} · {q.band}
                    {q.oxirgi_tashrif && tr(" · oxirgi tashrif {qanchaOtdi}", { qanchaOtdi: qanchaOtdi(q.oxirgi_tashrif) })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: '15px', fontWeight: 700,
                    color: q.shoshilinch ? '#d32f2f' : '#1a1a2e'
                  }}>
                    {q.tashrif_soni} / {q.kerak}
                  </div>
                  <div style={{ fontSize: '11px', color: q.shoshilinch ? '#d32f2f' : '#888' }}>
                    {tr("{qoldi} ta qoldi", { qoldi: q.qoldi })}{q.shoshilinch ? tr(" · shoshilinch") : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ---------------- NAZORATDAGILAR ---------------- */}
      {tab === 'mahkumlar' && (
        <div style={karta}>
          {mahkumlar.length === 0 && (
            <div style={{ color: '#888', fontSize: '14px' }}>
              {tr("Ozodlikni cheklash jazosidagi mahkum topilmadi. Mahkumlar reyestrida jazo turini \"Ozodlikni cheklash\" deb belgilang.")}
            </div>
          )}
          {mahkumlar.map((m) => {
            const hol = holatBelgisi(m);
            const sozlanmagan = !m.ochj_rejim || m.uy_lat == null;
            return (
              <div key={m.id} style={{
                border: '1px solid #eee', borderRadius: '10px',
                padding: '14px', marginBottom: '10px'
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start'
                }}>
                  <div style={{ flex: 1, minWidth: '240px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 600 }}>
                      {m.familiya} {m.ism} {m.sharif || ''}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                      {m.ochj_rejim ? REJIM_NOMI[m.ochj_rejim] : tr("Rejim belgilanmagan")}
                      {/* Hukmdagi soat — inspektor kartochkani ochmasdan ko'rsin:
                          kunduzgi chiqish buzilish emasligi shundan bilinadi. */}
                      {m.nazorat_boshlanish && m.nazorat_tugash
                        ? tr(" · nazorat {dan}–{gacha}", {
                            dan: String(m.nazorat_boshlanish).slice(0, 5),
                            gacha: String(m.nazorat_tugash).slice(0, 5) })
                        : tr(" · nazorat kecha-kunduz")}
                      {m.norma && tr(" · norma {oylik_tashrif}/{kerak}", { oylik_tashrif: m.oylik_tashrif, kerak: m.norma.kerak })}
                      {m.telefon && ` · ${m.telefon}`}
                    </div>
                    <div style={{
                      display: 'inline-block', marginTop: '8px', padding: '3px 10px',
                      borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                      background: `${hol.rang}18`, color: hol.rang
                    }}>
                      ● {hol.matn}
                    </div>
                    {m.nazorat_yoqilgan && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '5px' }}>
                        {tr("Oxirgi signal: {qanchaOtdi}", { qanchaOtdi: qanchaOtdi(m.signal_vaqti) })}
                        {m.batareya != null && tr(" · batareya {batareya}%", { batareya: m.batareya })}
                        {m.model && ` · ${m.model}`}
                      </div>
                    )}
                    {m.ochiq_hodisa > 0 && (
                      <div style={{ fontSize: '12px', color: '#d32f2f', marginTop: '4px' }}>
                        {tr("{ochiq_hodisa} ta ochiq ogohlantirish", { ochiq_hodisa: m.ochiq_hodisa })}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button onClick={() => setSozlama(m)} style={smallBtn}>{tr("Sozlash")}</button>
                    <button onClick={() => setXarita(m)} style={smallBtn}
                      disabled={!m.nazorat_yoqilgan}>{tr("Xarita")}</button>
                    {/* Uyga yo'l — tashrifga chiqayotgan xodim uchun.
                        Nazorat yoqilmagan bo'lsa ham ishlaydi: tashrif
                        ilovadan oldin ham o'tkaziladi. */}
                    <button onClick={() => setYol(m)} style={smallBtn}
                      title={tr("Uyga boradigan yo'lni navigatorda ochadi")}>
                      {tr("Yo'l")}
                    </button>
                    <button onClick={() => setTekshiruv(m)}
                      style={{ ...btn, background: '#2e7d32', padding: '6px 12px', fontSize: '12px' }}>
                      {tr("Tekshiruv")}
                    </button>
                    {m.nazorat_yoqilgan ? (
                      <>
                        <button onClick={() => onlaynBoshla(m)} disabled={band === `onlayn-${m.id}`}
                          style={{ ...btn, background: '#1976d2', padding: '6px 12px', fontSize: '12px' }}>
                          {tr("Hozir tekshir")}
                        </button>
                        <button onClick={() => setQongiroq(m)}
                          style={{ ...btn, background: '#00897b', padding: '6px 12px', fontSize: '12px' }}>
                          📞 {tr("Qo'ng'iroq")}
                        </button>
                        <button onClick={() => qurilmaUz(m)} disabled={band === `uz-${m.id}`}
                          style={{ ...btn, background: '#c62828', padding: '6px 12px', fontSize: '12px' }}
                          title={tr("Telefon almashtirilsa yoki ilova qayta o'rnatilsa")}>
                          {tr("Qurilmani uzish")}
                        </button>
                      </>
                    ) : (
                      <button onClick={() => kodBer(m)} disabled={band === `kod-${m.id}` || sozlanmagan}
                        style={{
                          ...btn, padding: '6px 12px', fontSize: '12px',
                          background: sozlanmagan ? '#bdbdbd' : '#7b1fa2'
                        }}
                        title={sozlanmagan ? tr("Avval rejim va uy manzilini sozlang") : ''}>
                        {tr("Ilova kodi")}
                      </button>
                    )}
                  </div>
                </div>

                {sozlanmagan && (
                  <div style={{
                    marginTop: '10px', background: RANG.sariq.fon, borderRadius: '8px',
                    padding: '9px 12px', fontSize: '12px', color: '#666'
                  }}>
                    {tr("Nazorat sozlanmagan:")} {!m.ochj_rejim && tr("jazo rejimi")}
                    {!m.ochj_rejim && m.uy_lat == null && tr(" va ")}
                    {m.uy_lat == null && tr("uy koordinatasi")} {tr("ko'rsatilmagan.")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}


      {/* ---------------- TEKSHIRUVLAR ---------------- */}
      {tab === 'tekshiruvlar' && (
        <div style={karta}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
            <h3 style={{ margin: 0, fontSize: '15px' }}>{tr("Tekshiruvlar tarixi")}</h3>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
              {[['royxat', tr("Ro'yxat")], ['selfi', tr("Selfilar")]].map(([k, nom]) => (
                <button key={k} onClick={() => setKorinish(k)} style={{
                  ...smallBtn,
                  background: korinish === k ? '#1a1a2e' : '#eef1f7',
                  color: korinish === k ? '#fff' : '#1a1a2e'
                }}>{nom}</button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '14px' }}>
            {korinish === 'selfi'
              ? tr("Onlayn tekshiruv selfilari kunlar bo'yicha saqlanadi. Qizil ramka — shubhali surat (orqa kamera yoki vaqti qayd etilmagan).")
              : tr("Har bir tekshiruv bo'yicha dalolatnoma chop etish mumkin (403-son buyruq, 84–85-bandlar).")}
          </div>

          {korinish === 'selfi' && (
            <SelfiArxiv tekshiruvlar={tekshiruvlar} token={token}
              ochish={(url) => setFoto(`${API}${url}`)} />
          )}

          {korinish === 'royxat' && tekshiruvlar.length === 0 && (
            <div style={{ color: '#888', fontSize: '14px' }}>
              {tr("Hali tekshiruv o'tkazilmagan.")}
            </div>
          )}

          {korinish === 'royxat' && (() => {
            // Mahkum bo'yicha guruhlaymiz: ilgari bitta mahkum ro'yxatda
            // o'nlab marta takrorlanib, boshqa mahkumlar ko'rinmay
            // qolardi. Endi har mahkum bitta qator — ustiga bosilganda
            // uning tekshiruvlari KUNMA-KUN ochiladi.
            const guruhlar = [];
            const xarita = new Map();
            for (const t of tekshiruvlar) {
              let g = xarita.get(t.mahkum_id);
              if (!g) {
                g = { mahkum_id: t.mahkum_id, familiya: t.familiya, ism: t.ism, royxat: [] };
                xarita.set(t.mahkum_id, g);
                guruhlar.push(g);
              }
              g.royxat.push(t);
            }

            return guruhlar.map((g) => {
              const ochiq = ochiqTekshiruv === g.mahkum_id;
              const buzilish = g.royxat.filter((t) => t.buzilish).length;
              const oxirgi = g.royxat[0];

              // Kunlar bo'yicha: har kun alohida sarlavha bilan.
              const kunlar = [];
              const kunXarita = new Map();
              for (const t of g.royxat) {
                const kun = new Date(t.created_at).toLocaleDateString('uz-UZ');
                let k = kunXarita.get(kun);
                if (!k) { k = { kun, royxat: [] }; kunXarita.set(kun, k); kunlar.push(k); }
                k.royxat.push(t);
              }

              return (
                <div key={g.mahkum_id} style={{
                  border: '1px solid #eee', borderRadius: '10px',
                  marginBottom: '8px', overflow: 'hidden'
                }}>
                  <div
                    onClick={() => setOchiqTekshiruv(ochiq ? null : g.mahkum_id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '12px 14px', cursor: 'pointer',
                      background: ochiq ? '#f6f8fb' : '#fff'
                    }}>
                    <span style={{ color: '#888', fontSize: '13px', width: '14px' }}>
                      {ochiq ? '▾' : '▸'}
                    </span>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        {g.familiya} {g.ism}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                        {tr("{soni} ta tekshiruv", { soni: g.royxat.length })}
                        {' · '}
                        {tr("oxirgisi {vaqt}", { vaqt: vaqtMatni(oxirgi.created_at) })}
                      </div>
                    </div>
                    {buzilish > 0 && (
                      <span style={{
                        fontSize: '11px', fontWeight: 600, padding: '3px 9px',
                        borderRadius: '20px', background: RANG.qizil.fon, color: RANG.qizil.matn
                      }}>
                        {tr("{soni} buzilish", { soni: buzilish })}
                      </span>
                    )}
                  </div>

                  {ochiq && (
                    <div style={{ padding: '4px 14px 12px', background: '#fafbfd' }}>
                      {kunlar.map((k) => (
                        <div key={k.kun}>
                          <div style={{
                            fontSize: '12px', fontWeight: 600, color: '#555',
                            margin: '10px 0 6px'
                          }}>
                            {k.kun}
                          </div>
                          {k.royxat.map((t) => (
                            <TekshiruvKarta key={t.id} t={t}
                              setFoto={setFoto} setDalolat={setDalolat} />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ---------------- PROKUROR ---------------- */}
      {tab === 'prokuror' && (
        <div style={karta}>
          <h3 style={{ marginTop: 0, fontSize: '15px' }}>{tr("24 soatlik xabarnomalar")}</h3>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '14px' }}>
            {tr("O'RQ-938 24-moddasi: mahkum uyiga kirilgan")} <b>{tr("har bir hol")}</b> {tr("haqida 24 soat ichida prokurorni")} <b>{tr("yozma yoki elektron shaklda")}</b> {tr("xabardor qilish shart (403-son buyruq, 127-band).")}
          </div>

          {prokuror.length === 0 && (
            <div style={{ color: '#2e7d32', fontSize: '14px' }}>
              {tr("Kutilayotgan xabarnoma yo'q.")}
            </div>
          )}

          {prokuror.map((p) => {
            const soat = Number(p.qolgan_soat);
            const shoshilinch = soat < 6;
            return (
              <div key={p.id} style={{
                background: shoshilinch ? RANG.qizil.fon : '#f7f8fa',
                borderLeft: `4px solid ${shoshilinch ? RANG.qizil.chiziq : '#bdbdbd'}`,
                borderRadius: '8px', padding: '12px 14px', marginBottom: '8px',
                display: 'flex', justifyContent: 'space-between',
                gap: '12px', flexWrap: 'wrap', alignItems: 'center'
              }}>
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>
                    {p.familiya} {p.ism}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '3px' }}>
                    {tr("Tashrif: {vaqtMatni}", { vaqtMatni: vaqtMatni(p.kirilgan_vaqt) })}
                    {p.manzil && ` · ${p.manzil}`}
                  </div>
                  <div style={{
                    fontSize: '13px', fontWeight: 600, marginTop: '4px',
                    color: shoshilinch ? '#d32f2f' : '#555'
                  }}>
                    {soat > 0
                      ? tr("{floor} soat {round} daqiqa qoldi", { floor: Math.floor(soat), round: Math.round((soat % 1) * 60) })
                      : tr("Muddat o'tib ketdi")}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button disabled={band === `prok-${p.id}`}
                    onClick={() => prokurorYubor(p, 'elektron')}
                    style={{ ...btn, background: '#1976d2', padding: '7px 13px', fontSize: '12px' }}>
                    {tr("Elektron yuborildi")}
                  </button>
                  <button disabled={band === `prok-${p.id}`}
                    onClick={() => prokurorYubor(p, 'yozma')} style={smallBtn}>
                    {tr("Yozma")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sozlama && (
        <SozlamaModal mahkum={sozlama} token={token}
          yop={() => setSozlama(null)} yangila={yukla} />
      )}
      {tekshiruv && (
        <TekshiruvModal mahkum={tekshiruv} token={token}
          yop={() => setTekshiruv(null)} yangila={yukla} />
      )}
      {yol && <YolKorsat mahkum={yol} onYopish={() => setYol(null)} />}

      {xarita && (
        <XaritaModal mahkum={xarita} token={token} yop={() => setXarita(null)} />
      )}
      {qongiroq && (
        <QongiroqModal mahkum={qongiroq} token={token}
          yop={() => setQongiroq(null)} yangila={yukla} />
      )}
      {foto && (
        <FotoModal url={foto.startsWith('http') ? foto : `${API.replace(/\/api$/, '')}${foto}`}
          token={token} yop={() => setFoto(null)} />
      )}
      {dalolat && (
        <DalolatnomaModal tekshiruv={dalolat} token={token}
          yop={() => setDalolat(null)} />
      )}
      {kod && (
        <Modal sarlavha={tr("Ilovani biriktirish kodi")} yop={() => setKod(null)}>
          <div style={{ textAlign: 'center', padding: '10px 0 20px' }}>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
              {tr("{familiya} {ism} uchun", { familiya: kod.mahkum.familiya, ism: kod.mahkum.ism })}
            </div>
            <div style={{
              fontSize: '38px', fontWeight: 700, letterSpacing: '6px',
              fontFamily: 'monospace', color: '#1a1a2e'
            }}>{kod.kod}</div>
          </div>
          <div style={{
            background: '#f3f8ff', borderRadius: '10px', padding: '14px', fontSize: '13px'
          }}>
            {kod.yoriqnoma}
            <ul style={{ margin: '10px 0 0', paddingLeft: '18px', color: '#555' }}>
              <li>{tr("Mahkum Play Market'dan")} <b>{tr("prob24")}</b> {tr("ilovasini o'rnatadi")}</li>
              <li>{tr("Shu kodni kiritadi — parol yoki ro'yxatdan o'tish kerak emas")}</li>
              <li>{tr("Joylashuvga")} <b>{tr("\"Doim ruxsat\"")}</b> {tr("beradi")}</li>
              <li>{tr("Batareya cheklovini o'chiradi (ilova o'zi so'raydi)")}</li>
            </ul>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button onClick={() => setKod(null)} style={{ ...btn, background: '#1976d2' }}>
              {tr("Yopish")}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Nazorat;
